import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  ShieldOff,
  Save,
  RefreshCcw,
  Filter,
  Download,
  CheckCircle2,
  XCircle,
  Users,
  Lock
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const backendUrl = "https://justurbane.in/backend_php/public";

const permissionFields = [
  { key: "login_permission", label: "Login" },
  { key: "article_management", label: "Articles" },
  { key: "gallery_management", label: "Gallery" },
  { key: "magazine_management", label: "Magazine" },
  { key: "orders_management", label: "Orders" },
  { key: "carousel_management", label: "Carousel" },
  { key: "article_categories_management", label: "Categories" }
];

const EmployeePermissions = () => {
  const navigate = useNavigate();

  // Core State
  const [employees, setEmployees] = useState([]);
  const [originalEmployees, setOriginalEmployees] = useState([]); // To track changes
  const [loading, setLoading] = useState(true);
  
  // UI State
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingChanges, setPendingChanges] = useState(new Set()); // IDs of edited employees
  const rowsPerPage = 10;

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchEmployees();
  }, [navigate]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${backendUrl}/api/admin/employees/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.status) {
        const data = res.data.data || [];
        setEmployees(JSON.parse(JSON.stringify(data))); // Deep clone
        setOriginalEmployees(JSON.parse(JSON.stringify(data)));
        setPendingChanges(new Set());
      }
    } catch {
      toast.error("Failed to load employee permissions");
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (id, key) => {
    const index = employees.findIndex(e => e.employee_id === id);
    const updated = [...employees];
    updated[index][key] = updated[index][key] === 1 ? 0 : 1;
    
    setEmployees(updated);
    
    // Check if current state differs from original to mark as pending
    const isDifferent = JSON.stringify(updated[index]) !== JSON.stringify(originalEmployees.find(e => e.employee_id === id));
    
    setPendingChanges(prev => {
      const next = new Set(prev);
      if (isDifferent) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const bulkToggle = (id, action) => {
    const index = employees.findIndex(e => e.employee_id === id);
    const updated = [...employees];
    const val = action === 'enable' ? 1 : 0;
    
    permissionFields.forEach(field => {
      updated[index][field.key] = val;
    });
    
    setEmployees(updated);
    setPendingChanges(prev => new Set(prev).add(id));
  };

  const savePermissions = async (employee) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${backendUrl}/api/admin/employee-permissions/update`, employee, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      toast.success(`Permissions updated for ${employee.employee_name}`);
      
      // Update original state for this employee
      const newOriginals = originalEmployees.map(e => 
        e.employee_id === employee.employee_id ? { ...employee } : e
      );
      setOriginalEmployees(newOriginals);
      
      setPendingChanges(prev => {
        const next = new Set(prev);
        next.delete(employee.employee_id);
        return next;
      });
    } catch {
      toast.error("Failed to update permissions");
    }
  };

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Role", ...permissionFields.map(p => p.label)];
    const rows = filteredEmployees.map(e => [
      e.employee_name,
      e.email,
      e.type_name,
      ...permissionFields.map(p => e[p.key] === 1 ? "Yes" : "No")
    ]);
    
    const content = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Employee_Permissions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Helper for Search Highlighting
  const Highlight = ({ text, query }) => {
    if (!query) return <span>{text}</span>;
    const parts = String(text).split(new RegExp(`(${query})`, "gi"));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() 
            ? <mark key={i} className="bg-amber-200 text-amber-900 rounded-sm px-0.5">{part}</mark> 
            : part
        )}
      </span>
    );
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter(e => {
      const matchesSearch = !search || 
        e.employee_name.toLowerCase().includes(search.toLowerCase()) ||
        e.email.toLowerCase().includes(search.toLowerCase()) ||
        e.type_name.toLowerCase().includes(search.toLowerCase());
      
      const matchesRole = roleFilter === "All" || e.type_name === roleFilter;
      
      return matchesSearch && matchesRole;
    });
  }, [employees, search, roleFilter]);

  const uniqueRoles = useMemo(() => 
    ["All", ...new Set(originalEmployees.map(e => e.type_name))], 
  [originalEmployees]);

  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredEmployees.slice(start, start + rowsPerPage);
  }, [filteredEmployees, currentPage]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-medium animate-pulse">Syncing Permissions...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Header */}
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin/dashboard")} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="text-amber-500 w-6 h-6" />
                Access Control Center
              </h1>
              <p className="text-xs text-gray-500 font-medium">Manage employee module permissions</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-all">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={fetchEmployees} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-200 text-sm font-medium transition-all">
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-xl"><Users className="text-blue-600 w-6 h-6" /></div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Staff</p>
              <h3 className="text-2xl font-bold text-gray-900">{employees.length}</h3>
            </div>
          </div>
         </div>

        {/* Toolbar */}
        <div className="bg-white rounded-t-2xl border border-b-0 p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Search by name, email, or role..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all outline-none text-sm"
            />
          </div>
          
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mr-2">
              <Filter className="w-4 h-4" /> Filter Role:
            </div>
            <select 
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-500/20 transition-all cursor-pointer"
            >
              {uniqueRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-b-2xl border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Employee Info</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                  {permissionFields.map(p => (
                    <th key={p.key} className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">
                      {p.label}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {paginatedData.map((emp) => {
                  const hasChanges = pendingChanges.has(emp.employee_id);
                  return (
                    <tr 
                      key={emp.employee_id} 
                      className={`group transition-colors ${hasChanges ? 'bg-amber-50/30' : 'hover:bg-gray-50/50'}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${hasChanges ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                            {emp.employee_name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 leading-none mb-1">
                              <Highlight text={emp.employee_name} query={search} />
                            </div>
                            <div className="text-xs text-gray-500"><Highlight text={emp.email} query={search} /></div>
                            {hasChanges && (
                              <span className="text-[10px] font-bold text-amber-600 flex items-center gap-1 mt-1">
                                <RefreshCcw className="w-2 h-2" /> UNSAVED CHANGES
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm font-medium text-gray-600">
                        <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 border border-gray-200">
                          <Highlight text={emp.type_name} query={search} />
                        </span>
                      </td>

                      {permissionFields.map(p => (
                        <td key={p.key} className="px-4 py-4 text-center">
                          <button
                            onClick={() => togglePermission(emp.employee_id, p.key)}
                            className={`group relative inline-flex items-center justify-center w-10 h-10 rounded-xl transition-all border ${
                              emp[p.key] === 1
                                ? "bg-green-50 text-green-600 border-green-100 hover:bg-green-100"
                                : "bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100"
                            }`}
                          >
                            {emp[p.key] === 1 ? <ShieldCheck className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                              Toggle {p.label}
                            </span>
                          </button>
                        </td>
                      ))}

                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {/* Bulk Actions Mini-Menu */}
                          <div className="flex flex-col gap-1 mr-2">
                             <button onClick={() => bulkToggle(emp.employee_id, 'enable')} className="text-[9px] font-bold text-green-600 hover:underline">ALL ON</button>
                             <button onClick={() => bulkToggle(emp.employee_id, 'disable')} className="text-[9px] font-bold text-red-600 hover:underline">ALL OFF</button>
                          </div>
                          
                          <button
                            onClick={() => savePermissions(emp)}
                            disabled={!hasChanges}
                            className={`p-2.5 rounded-xl transition-all shadow-sm ${
                              hasChanges 
                              ? "bg-blue-600 text-white hover:bg-blue-700 hover:scale-110 shadow-blue-200" 
                              : "bg-gray-100 text-gray-300 cursor-not-allowed"
                            }`}
                          >
                            <Save className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan={permissionFields.length + 3} className="py-20 text-center">
                      <div className="flex flex-col items-center">
                        <div className="p-4 bg-gray-50 rounded-full mb-4"><Search className="w-8 h-8 text-gray-300" /></div>
                        <p className="text-gray-500 font-medium">No employees matching your criteria</p>
                        <button onClick={() => {setSearch(""); setRoleFilter("All");}} className="mt-2 text-amber-600 text-sm font-bold">Clear all filters</button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
            <p className="text-sm text-gray-500 font-medium">
              Showing <span className="text-gray-900">{paginatedData.length}</span> of <span className="text-gray-900">{filteredEmployees.length}</span> employees
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-200 bg-white rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-1 mx-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${
                      currentPage === i + 1 
                      ? "bg-amber-500 text-white shadow-md shadow-amber-200" 
                      : "hover:bg-gray-100 text-gray-600"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-2 border border-gray-200 bg-white rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeePermissions;